import { OPENING, type Opening } from './openings';

/**
 * Walls with rectangular openings, triangulated directly in wall coordinates (x along the wall
 * from its left end seen from outside, y up) without earcut.
 *
 * The openings' bounding box G is cut into rows of openings with bands of wall between them;
 * each row is cut into piers and opening columns. Four pieces join G to the outline, so the
 * outline carries no vertex but its corners and the corner, eaves and verge edges meet the
 * neighbouring walls and roofs vertex to vertex. Inside, every piece takes over the vertices of
 * its neighbours on the lines it shares with them, so the mesh has no T-junctions to crack.
 */

export interface Hole {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** Heights strictly inside the hole where its infill changes, bottom to top. */
  readonly cuts: readonly number[];
}

/** One horizontal slice of the infill of a hole. */
export interface Pane {
  readonly hole: number;
  readonly slice: number;
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

export type Pt = readonly [number, number];

/** A convex fan of wall triangles: the first point joined to each edge of the rest. */
export type Fan = readonly Pt[];

export interface WallMesh {
  /** Wall as fans, counter-clockwise seen from outside. */
  readonly wall: readonly Fan[];
  readonly panes: readonly Pane[];
}

interface Box {
  readonly index: number;
  readonly x0: number;
  readonly x1: number;
  readonly y0: number;
  readonly y1: number;
  readonly cuts: readonly number[];
}

interface Row {
  readonly y0: number;
  readonly y1: number;
  readonly boxes: readonly Box[];
}

interface Interior {
  readonly x0: number;
  readonly x1: number;
  readonly rows: readonly Row[];
  /** x of the vertices along the top and bottom lines of each row. */
  readonly lines: readonly (readonly number[])[];
}

// Opening edges come from sums of storey heights and rounded bay widths, so edges that line up
// on the wall can miss each other by a hair; edges within a millimetre are welded into one, which
// also keeps sub-millimetre slivers of wall out of the mesh.
const WELD = 1e-3;

type Weld = (v: number) => number;

/* A wall has a few dozen edges at most: sorted by insertion and looked up in a list, faster than a map. */
function welder(values: readonly number[]): Weld {
  const sorted: number[] = [];
  for (const v of values) {
    let j = sorted.length;
    sorted.push(v);
    while (j > 0 && sorted[j - 1]! > v) {
      sorted[j] = sorted[j - 1]!;
      j--;
    }
    sorted[j] = v;
  }
  const anchors: number[] = [];
  let anchor = sorted[0]!;
  for (const v of sorted) {
    if (v - anchor > WELD) anchor = v;
    anchors.push(anchor);
  }
  return (v) => anchors[sorted.indexOf(v)]!;
}

/**
 * Where an opening's infill changes: a door's leaf meets its transom light, and a cross window's
 * lower lights, open behind their shutters, meet its glazed upper ones on the transom's middle.
 */
function cutsOf(o: Opening): number[] {
  if (o.kind === 'window') return [o.y + OPENING.transomAt * o.height];
  return o.leaf !== undefined && o.leaf < o.height ? [o.y + o.leaf] : [];
}

export function holeOf(o: Opening): Hole {
  return { x: o.x, y: o.y, width: o.width, height: o.height, cuts: cutsOf(o) };
}

function boxOf(hole: Hole, index: number, wx: Weld, wy: Weld): Box {
  return {
    index,
    x0: wx(hole.x),
    x1: wx(hole.x + hole.width),
    y0: wy(hole.y),
    y1: wy(hole.y + hole.height),
    cuts: hole.cuts.map(wy),
  };
}

/** Openings whose heights overlap or touch share a row. */
function rowsOf(boxes: readonly Box[]): Row[] {
  const sorted = [...boxes].sort((a, b) => a.y0 - b.y0);
  const rows: { y0: number; y1: number; boxes: Box[] }[] = [];
  for (const box of sorted) {
    const last = rows[rows.length - 1];
    if (last && box.y0 <= last.y1) {
      last.y1 = Math.max(last.y1, box.y1);
      last.boxes.push(box);
    } else rows.push({ y0: box.y0, y1: box.y1, boxes: [box] });
  }
  return rows.map((r) => ({ ...r, boxes: [...r.boxes].sort((a, b) => a.x0 - b.x0) }));
}

/** A run of triangles sharing an apex as one fan, wound counter-clockwise. */
function fanOf(apex: Pt, rim: readonly Pt[], ccw: boolean): Fan {
  if (ccw) return [apex, ...rim];
  const fan: Pt[] = [apex];
  for (let k = rim.length - 1; k >= 0; k--) fan.push(rim[k]!);
  return fan;
}

/**
 * Triangulates the strip between two chains that run the same way along `axis`, taking the
 * nearer next vertex each step so every vertex of both chains is used, and hands the
 * triangles on as fans: each step keeps one chain's vertex as the apex until the other runs on.
 */
function stitch(out: Fan[], a: readonly Pt[], b: readonly Pt[], axis: 0 | 1): void {
  let apex: Pt | null = null;
  let rim: Pt[] = [];
  let ccw = false;
  let i = 0;
  let j = 0;
  while (i < a.length - 1 || j < b.length - 1) {
    const takeA = j === b.length - 1 || (i < a.length - 1 && a[i + 1]![axis] <= b[j + 1]![axis]);
    const tip = takeA ? b[j]! : a[i]!;
    const p = takeA ? a[i]! : b[j]!;
    const q = takeA ? a[i + 1]! : b[j + 1]!;
    const area = (p[0] - tip[0]) * (q[1] - tip[1]) - (p[1] - tip[1]) * (q[0] - tip[0]);
    const turn = area > 0;
    if (apex === tip && rim[rim.length - 1] === p && ccw === turn && area !== 0) {
      rim.push(q);
    } else {
      if (apex) out.push(fanOf(apex, rim, ccw));
      apex = area === 0 ? null : tip;
      rim = [p, q];
      ccw = turn;
    }
    if (takeA) i++;
    else j++;
  }
  if (apex) out.push(fanOf(apex, rim, ccw));
}

function unique(values: readonly number[]): number[] {
  return values.filter((v, i) => i === 0 || v !== values[i - 1]);
}

function vertical(x: number, ys: readonly number[]): Pt[] {
  return ys.map((y): Pt => [x, y]);
}

function horizontal(xs: readonly number[], y: number): Pt[] {
  return xs.map((x): Pt => [x, y]);
}

/** Heights of the vertices down one side of an opening column. */
function sideOf(box: Box, row: Row): number[] {
  return unique([row.y0, box.y0, ...box.cuts, box.y1, row.y1]);
}

/** x of the vertices along the top or bottom line of a row. */
function lineOf(row: Row, x0: number, x1: number): number[] {
  const xs = [x0];
  for (const b of row.boxes) xs.push(b.x0, b.x1);
  xs.push(x1);
  return unique(xs);
}

function quad(out: Fan[], x0: number, y0: number, x1: number, y1: number): void {
  if (x1 <= x0 || y1 <= y0) return;
  out.push([
    [x0, y0],
    [x1, y0],
    [x1, y1],
    [x0, y1],
  ]);
}

function panesOf(box: Box): Pane[] {
  const ys = unique([box.y0, ...box.cuts, box.y1]);
  return ys
    .slice(1)
    .map((y1, slice) => ({ hole: box.index, slice, x0: box.x0, y0: ys[slice]!, x1: box.x1, y1 }));
}

function meshRow(out: Fan[], panes: Pane[], row: Row, g: Interior): void {
  let left = g.x0;
  let leftSide = [row.y0, row.y1];
  for (const box of row.boxes) {
    const side = sideOf(box, row);
    if (box.x0 > left) stitch(out, vertical(left, leftSide), vertical(box.x0, side), 1);
    quad(out, box.x0, row.y0, box.x1, box.y0);
    quad(out, box.x0, box.y1, box.x1, row.y1);
    panes.push(...panesOf(box));
    left = box.x1;
    leftSide = side;
  }
  if (g.x1 > left) stitch(out, vertical(left, leftSide), vertical(g.x1, [row.y0, row.y1]), 1);
}

/** Heights of the vertices down the left (or right) edge of G. */
function edgeOf(g: Interior, end: 'left' | 'right'): number[] {
  const ys: number[] = [];
  for (const row of g.rows) {
    const box = end === 'left' ? row.boxes[0]! : row.boxes[row.boxes.length - 1]!;
    const flush = end === 'left' ? box.x0 === g.x0 : box.x1 === g.x1;
    if (flush) ys.push(...sideOf(box, row));
    else ys.push(row.y0, row.y1);
  }
  return unique(ys);
}

function meshInterior(out: Fan[], g: Interior): Pane[] {
  const panes: Pane[] = [];
  g.rows.forEach((row, k) => {
    meshRow(out, panes, row, g);
    const next = g.rows[k + 1];
    if (next) stitch(out, horizontal(g.lines[k]!, row.y1), horizontal(g.lines[k + 1]!, next.y0), 0);
  });
  return panes;
}

function interiorOf(holes: readonly Hole[]): Interior {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const h of holes) {
    xs.push(h.x, h.x + h.width);
    ys.push(h.y, h.y + h.height, ...h.cuts);
  }
  const wx = welder(xs);
  const wy = welder(ys);
  const boxes = holes.map((hole, index) => boxOf(hole, index, wx, wy));
  const x0 = boxes.reduce((min, b) => Math.min(min, b.x0), Infinity);
  const x1 = boxes.reduce((max, b) => Math.max(max, b.x1), -Infinity);
  const rows = rowsOf(boxes);
  return { x0, x1, rows, lines: rows.map((row) => lineOf(row, x0, x1)) };
}

/** A width by height wall standing on y = 0; the openings lie inside it and off its top. */
export function meshRectWall(width: number, height: number, holes: readonly Hole[]): WallMesh {
  const out: Fan[] = [];
  if (holes.length === 0) {
    quad(out, 0, 0, width, height);
    return { wall: out, panes: [] };
  }
  const g = interiorOf(holes);
  const first = g.rows[0]!;
  const last = g.rows[g.rows.length - 1]!;
  const panes = meshInterior(out, g);
  if (first.y0 > 0)
    stitch(
      out,
      [
        [0, 0],
        [width, 0],
      ],
      horizontal(g.lines[0]!, first.y0),
      0
    );
  stitch(
    out,
    horizontal(g.lines[g.rows.length - 1]!, last.y1),
    [
      [0, height],
      [width, height],
    ],
    0
  );
  stitch(
    out,
    [
      [0, 0],
      [0, height],
    ],
    vertical(g.x0, edgeOf(g, 'left')),
    1
  );
  stitch(
    out,
    vertical(g.x1, edgeOf(g, 'right')),
    [
      [width, 0],
      [width, height],
    ],
    1
  );
  return { wall: out, panes };
}

/**
 * The triangle of a gable over a wall `width` wide, from the eaves line to the ridge above its
 * middle; the openings lie inside it.
 */
export function meshGableWall(
  width: number,
  eaves: number,
  ridge: number,
  holes: readonly Hole[]
): WallMesh {
  const out: Fan[] = [];
  const apex: Pt = [width / 2, ridge];
  if (holes.length === 0) {
    out.push([[0, eaves], [width, eaves], apex]);
    return { wall: out, panes: [] };
  }
  const g = interiorOf(holes);
  const first = g.rows[0]!;
  const last = g.rows[g.rows.length - 1]!;
  const panes = meshInterior(out, g);
  stitch(
    out,
    [
      [0, eaves],
      [width, eaves],
    ],
    horizontal(g.lines[0]!, first.y0),
    0
  );
  stitch(out, horizontal(g.lines[g.rows.length - 1]!, last.y1), [apex], 0);
  stitch(out, [[0, eaves], apex], vertical(g.x0, edgeOf(g, 'left')), 1);
  stitch(out, vertical(g.x1, edgeOf(g, 'right')), [[width, eaves], apex], 1);
  return { wall: out, panes };
}
